'use strict'
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'

import { CircularProgress } from '@mui/material'

import { useEmployeeCountDispatch } from '../extra/store'
import { formatNumberValue } from '../../../../common/utils/NumberUtils'

/*==================================
 コンポーネント
 ==================================*/
/**
 * 営業日数一括設定ポップアップコンポーネント。
 *
 * @param {{
 *   open: boolean
 *   onSave: (value: string) => Promise<void>
 *   onClose: () => void
 * }} props
 * @returns {React.ReactElement | null}
 */
const BulkSetWorkingDaysPopover = ({ open, onSave, onClose }) => {
  const [value, setValue] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // ポップアップ要素への参照
  const popoverRef = useRef(null)

  /**
   * @function handleInputChange 入力値変更ハンドラ
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const handleInputChange = useCallback((e) => {
    setValue(e.target.value)
    setErrorMessage('')
  }, [])

  /**
   * @function handleInputBlur 入力欄からフォーカスが外れた時のハンドラ
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const handleInputBlur = useCallback((e) => {
    const trimmedValue = e.target.value.trim()
    const formattedValue = formatNumberValue(trimmedValue, 0, false)
    setValue(formattedValue !== null ? String(formattedValue) : '')
    setErrorMessage('')
  }, [])

  /** @function handleSave 保存ハンドラ */
  const handleSave = async () => {
    if (isSaving) return // 二重送信防止

    setErrorMessage('')
    setIsSaving(true)
    try {
      // トリム処理
      const trimmedValue = value.trim()
      await onSave?.(trimmedValue)
      onClose() // 成功時にポップアップを閉じる
    } catch (error) {
      setErrorMessage('保存に失敗しました。')
    } finally {
      setIsSaving(false)
    }
  }

  // ポップアップが開いたときに状態をリセット
  useEffect(() => {
    if (open) {
      setErrorMessage('')
      setValue('')
    }
  }, [open])

  // ポップアップ外をクリックで閉じるロジック
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={popoverRef}
      className="absolute shadow-md border border-gray-300/50 z-50 top-[50%] translate-y-[20%] text-sm rounded bg-white w-max"
    >
      <div className="flex justify-end">
        <button type="button" onClick={onClose} className="relative -translate-x-[70%] translate-y-[30%]">
          ×
        </button>
      </div>

      <label className="p-3 pb-0" htmlFor="bulk-set-working-day-input">
        営業日数を一括設定
      </label>
      <div className="flex gap-x-2 p-3 pb-4">
        <input
          type="text"
          name="bulkSetworkingDay"
          id="bulk-set-working-day-input"
          value={value}
          placeholder="数値を入力"
          className="rounded-lg text-input w-[200px]"
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
        <button
          className="underline text-blue-500 hover:bg-blue-300/30 py-1 px-2 rounded-lg shrink-0 flex items-center gap-x-2 disabled:cursor-not-allowed"
          disabled={!value.trim() || isSaving}
          onClick={handleSave}
        >
          {isSaving && <CircularProgress size={16} className="text-gray-700" />}
          設定
        </button>

        <button
          className="underline text-blue-500 hover:bg-blue-300/30 py-1 px-2 text-sm rounded-lg shrink-0"
          onClick={onClose}
        >
          キャンセル
        </button>
      </div>
      <p className="text-red-500 text-sm px-3 mb-2">{errorMessage}</p>
    </div>
  )
}

/**
 * 営業日数一括設定機能の親コンポーネント。 ポップアップの表示とロジックを管理します。
 *
 * @param {{ isDisabled: boolean }} props
 * @returns {React.ReactElement}
 */
const BulkSetWorkingDays = ({ isDisabled }) => {
  const dispatch = useEmployeeCountDispatch()
  const [showPopover, setShowPopover] = useState(false)
  const [showApplyText, setShowApplyText] = useState({ text: '', state: 'normal' })
  const savingRequestInProgress = useRef(false)

  // ポップアップの表示・非表示を切り替える
  const togglePopover = useCallback(() => {
    setShowPopover((prev) => !prev)
  }, [])

  // ポップアップを閉じる
  const closePopover = useCallback(() => {
    setShowPopover(false)
  }, [])

  /**
   * @function handleBulkSetWorkingDays 営業日数一括設定の保存ロジック
   * @param {string} value
   * @returns {Promise<void>}
   */
  const handleBulkSetWorkingDays = async (value) => {
    // useRefによる二重送信防止
    if (savingRequestInProgress.current) {
      return
    }
    savingRequestInProgress.current = true

    try {
      dispatch('BULK_SET_WORKING_DAY', { value })

      setShowApplyText({
        text: '営業日数を一括設定しました。',
        state: 'success'
      })
      setTimeout(() => {
        setShowApplyText({ text: '', state: 'normal' })
      }, 4000)
    } catch (error) {
      // エラーハンドリング
      setShowApplyText({
        text: '設定に失敗しました。',
        state: 'error'
      })
      setTimeout(() => {
        setShowApplyText({ text: '', state: 'normal' })
      }, 4000)
      throw error
    } finally {
      savingRequestInProgress.current = false
    }
  }

  const textColor = useMemo(
    () => ({
      success: 'text-green-500',
      error: 'text-red-500',
      normal: 'text-gray-700/50'
    }),
    []
  )

  return (
    <>
      <div className="flex gap-x-2 items-center justify-end relative">
        <div className="relative inline-block">
          <button
            type="button"
            className={`text-blue-500 underline ml-5  ${isDisabled ? 'hidden' : ''}`}
            onClick={togglePopover}
            disabled={isDisabled}
          >
            営業日数を一括設定
          </button>
          <BulkSetWorkingDaysPopover open={showPopover} onSave={handleBulkSetWorkingDays} onClose={closePopover} />
        </div>
        <p
          className={
            `mt-3 ${textColor[showApplyText.state] ?? ''} absolute top-[50%]` +
            ` whitespace-nowrap left-[10%] ${showApplyText.text ? 'visible' : 'invisible'}`
          }
        >
          {showApplyText.text}
        </p>
      </div>
    </>
  )
}

export default BulkSetWorkingDays
